import React from 'react';
import { MachineData } from '../types/types';
import { Card } from './UI/Card';
import { LineChart } from './charts/LineChart';
import { GaugeChart } from './charts/GaugeChart';
import { StatusBadge } from './UI/StatusBadge';
import { MachineDetails } from './MachineDetails';
import { formatNumber, formatDecimal } from '../utils/formatters';
import { Activity, Clock, AlertTriangle, Thermometer, Droplets, Wind, Package, Zap, Brush, Cylinder, Stamp, Fuel, Recycle, AlertCircle } from 'lucide-react';
import { WaschanlageBarrelTracking } from './WaschanlageBarrelTracking';
import { BarrelLabelImages } from './BarrelLabelImages';
import { LackieranlageDetail } from './LackieranlageDetail';
import { AusbeulDashboard } from './AusbeulDashboard';
import { WaschDashboard } from './WaschDashboard';
import { RoboterDashboard } from './RoboterDashboard';
import { BuersteDashboard } from './BuersteDashboard';
import { SandstrahlDashboard } from './SandstrahlDashboard';
import { DichtpruefungDashboard } from './DichtpruefungDashboard';
import { TrocknungDashboard } from './TrocknungDashboard';
import { SignierDashboard } from './SignierDashboard';
import { LackierDashboard } from './LackierDashboard';
import { SiebdruckDashboard } from './SiebdruckDashboard';
interface DashboardProps {
  machine: MachineData;
}
// Funktion zur Bestimmung der Rohstoffverbrauchsdaten basierend auf dem Maschinentyp
const getResourceConsumption = (machineType: string, efficiency: number) => {
  // Effizienzfaktor: Je höher die Effizienz, desto niedriger der Verbrauch
  const efficiencyFactor = (100 - efficiency) / 100 + 0.5;
  switch (machineType) {
    case 'ausbeulanlage':
      return [{
        name: 'Hydrauliköl',
        amount: (0.2 * efficiencyFactor).toFixed(2),
        unit: 'L/Fass',
        icon: <Droplets size={20} className="text-blue-400" data-id="element-128" />
      }, {
        name: 'Strom',
        amount: (1.5 * efficiencyFactor).toFixed(2),
        unit: 'kWh/Fass',
        icon: <Zap size={20} className="text-yellow-400" data-id="element-129" />
      }];
    case 'waschanlage':
      return [{
        name: 'Wasser',
        amount: (12 * efficiencyFactor).toFixed(1),
        unit: 'L/Fass',
        icon: <Droplets size={20} className="text-blue-400" data-id="element-130" />
      }, {
        name: 'Reinigungsmittel',
        amount: (0.3 * efficiencyFactor).toFixed(2),
        unit: 'L/Fass',
        icon: <Package size={20} className="text-green-400" data-id="element-131" />
      }, {
        name: 'Strom',
        amount: (0.8 * efficiencyFactor).toFixed(2),
        unit: 'kWh/Fass',
        icon: <Zap size={20} className="text-yellow-400" data-id="element-132" />
      }];
    case 'roboterarm':
      return [{
        name: 'Strom',
        amount: (2.5 * efficiencyFactor).toFixed(2),
        unit: 'kWh/Fass',
        icon: <Zap size={20} className="text-yellow-400" data-id="element-133" />
      }, {
        name: 'Schmiermittel',
        amount: (0.05 * efficiencyFactor).toFixed(3),
        unit: 'L/Fass',
        icon: <Droplets size={20} className="text-gray-400" data-id="element-134" />
      }];
    case 'faesserBuerste':
      return [{
        name: 'Bürstenabnutzung',
        amount: (0.02 * efficiencyFactor).toFixed(3),
        unit: 'Einheiten/Fass',
        icon: <Brush size={20} className="text-purple-400" data-id="element-135" />
      }, {
        name: 'Strom',
        amount: (0.6 * efficiencyFactor).toFixed(2),
        unit: 'kWh/Fass',
        icon: <Zap size={20} className="text-yellow-400" data-id="element-136" />
      }, {
        name: 'Wasser',
        amount: (2 * efficiencyFactor).toFixed(1),
        unit: 'L/Fass',
        icon: <Droplets size={20} className="text-blue-400" data-id="element-137" />
      }];
    case 'sandstrahlmaschine':
      return [{
        name: 'Sand',
        amount: (3.5 * efficiencyFactor).toFixed(2),
        unit: 'kg/Fass',
        icon: <Wind size={20} className="text-amber-400" data-id="element-138" />
      }, {
        name: 'Druckluft',
        amount: (15 * efficiencyFactor).toFixed(1),
        unit: 'm³/Fass',
        icon: <Wind size={20} className="text-blue-300" data-id="element-139" />
      }, {
        name: 'Strom',
        amount: (1.2 * efficiencyFactor).toFixed(2),
        unit: 'kWh/Fass',
        icon: <Zap size={20} className="text-yellow-400" data-id="element-140" />
      }];
    case 'buersteMaschine':
      return [{
        name: 'Bürstenabnutzung',
        amount: (0.03 * efficiencyFactor).toFixed(3),
        unit: 'Einheiten/Fass',
        icon: <Brush size={20} className="text-purple-400" data-id="element-141" />
      }, {
        name: 'Strom',
        amount: (0.7 * efficiencyFactor).toFixed(2),
        unit: 'kWh/Fass',
        icon: <Zap size={20} className="text-yellow-400" data-id="element-142" />
      }];
    case 'dichtpruefungsmaschine':
      return [{
        name: 'Druckluft',
        amount: (8 * efficiencyFactor).toFixed(1),
        unit: 'm³/Fass',
        icon: <Wind size={20} className="text-blue-300" data-id="element-143" />
      }, {
        name: 'Dichtmittel',
        amount: (0.1 * efficiencyFactor).toFixed(2),
        unit: 'L/Fass',
        icon: <Cylinder size={20} className="text-gray-400" data-id="element-144" />
      }, {
        name: 'Strom',
        amount: (0.5 * efficiencyFactor).toFixed(2),
        unit: 'kWh/Fass',
        icon: <Zap size={20} className="text-yellow-400" data-id="element-145" />
      }];
    case 'trocknung':
      return [{
        name: 'Erdgas',
        amount: (0.4 * efficiencyFactor).toFixed(2),
        unit: 'm³/Fass',
        icon: <Thermometer size={20} className="text-red-400" data-id="element-146" />
      }, {
        name: 'Strom',
        amount: (1.8 * efficiencyFactor).toFixed(2),
        unit: 'kWh/Fass',
        icon: <Zap size={20} className="text-yellow-400" data-id="element-147" />
      }];
    case 'signiermaschine':
      return [{
        name: 'Tinte',
        amount: (0.05 * efficiencyFactor).toFixed(3),
        unit: 'L/Fass',
        icon: <Stamp size={20} className="text-indigo-400" data-id="element-148" />
      }, {
        name: 'Strom',
        amount: (0.3 * efficiencyFactor).toFixed(2),
        unit: 'kWh/Fass',
        icon: <Zap size={20} className="text-yellow-400" data-id="element-149" />
      }];
    default:
      return [{
        name: 'Strom',
        amount: (1.0 * efficiencyFactor).toFixed(2),
        unit: 'kWh/Fass',
        icon: <Zap size={20} className="text-yellow-400" data-id="element-150" />
      }];
  }
};
// Funktion zur Bestimmung der Abfalldaten basierend auf dem Maschinentyp
const getWasteProduction = (machineType: string, efficiency: number) => {
  // Effizienzfaktor: Je höher die Effizienz, desto niedriger der Abfall
  const efficiencyFactor = (100 - efficiency) / 100 + 0.5;
  switch (machineType) {
    case 'waschanlage':
      return [{
        name: 'Säurehaltige Chemikalien',
        amount: (0.15 * efficiencyFactor).toFixed(2),
        unit: 'L/Fass',
        icon: <Droplets size={20} className="text-purple-400" data-id="element-151" />
      }, {
        name: 'Öle & Schmierstoffe',
        amount: (0.08 * efficiencyFactor).toFixed(2),
        unit: 'L/Fass',
        icon: <Fuel size={20} className="text-yellow-300" data-id="element-152" />
      }];
    case 'ausbeulanlage':
      return [{
        name: 'Altöl',
        amount: (0.05 * efficiencyFactor).toFixed(2),
        unit: 'L/Fass',
        icon: <Fuel size={20} className="text-yellow-300" data-id="element-153" />
      }];
    case 'roboterarm':
      return [{
        name: 'Verbrauchte Schmierstoffe',
        amount: (0.03 * efficiencyFactor).toFixed(2),
        unit: 'L/Fass',
        icon: <Fuel size={20} className="text-yellow-300" data-id="element-154" />
      }];
    case 'sandstrahlmaschine':
      return [{
        name: 'Verbrauchter Sand',
        amount: (3.2 * efficiencyFactor).toFixed(2),
        unit: 'kg/Fass',
        icon: <Recycle size={20} className="text-amber-400" data-id="element-155" />
      }];
    default:
      return [];
  }
};
export const Dashboard: React.FC<DashboardProps> = ({
  machine
}) => {
  // Berechne den Durchsatz für die Maschine
  const baseRate = Math.round(machine.metrics.efficiency / 10 * (machine.metrics.currentLoad / 100) * 10) / 10;
  const ratePerHour = Math.round(baseRate * 60);
  // Hole die Rohstoffverbrauchsdaten
  const resourceConsumption = getResourceConsumption(machine.type, machine.metrics.efficiency);
  // Hole die Abfalldaten
  const wasteProduction = getWasteProduction(machine.type, machine.metrics.efficiency);
  // Berechne den Gesamtverbrauch pro Stunde
  const calculateHourlyConsumption = (amount: string, unit: string) => {
    const numAmount = parseFloat(amount);
    return formatDecimal(numAmount * ratePerHour, 2) + ' ' + unit.replace('/Fass', '/h');
  };
  const renderMachineSpecificDetails = () => {
    switch (machine.type) {
      case 'lackieranlage':
        return <LackieranlageDetail machine={machine} data-id="element-156" />;
      default:
        return null;
    }
  };
  return <div className="space-y-6" data-id="element-157">
      <div className="flex items-center justify-between" data-id="element-158">
        <div data-id="element-159">
          <h2 className="text-2xl font-bold text-white" data-id="element-160">{machine.name}</h2>
          <p className="text-gray-400" data-id="element-161">Echtzeitüberwachung und Analyse</p>
        </div>
        <StatusBadge status={machine.status} data-id="element-162" />
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4" data-id="element-163">
        <Card title="Betriebszeit" icon={<Clock className="text-blue-400" data-id="element-165" />} value={`${formatNumber(machine.metrics.operatingHours)}h`} data-id="element-164" />
        <Card title="Effizienz" icon={<Activity className="text-green-400" data-id="element-167" />} value={`${machine.metrics.efficiency}%`} data-id="element-166" />
        <Card title="Warnungen" icon={<AlertTriangle className="text-yellow-400" data-id="element-169" />} value={machine.metrics.warnings.toString()} data-id="element-168" />
        <Card title="Temperatur" icon={<Thermometer className="text-red-400" data-id="element-171" />} value={`${machine.metrics.temperature}°C`} data-id="element-170" />
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6" data-id="element-172">
        <div className="bg-gray-800 rounded-xl p-4 border border-gray-700 shadow-lg backdrop-blur-sm bg-opacity-60" data-id="element-173">
          <h3 className="text-lg font-medium mb-4 text-gray-200" data-id="element-174">
            Leistungsverlauf (24h)
          </h3>
          <div className="h-64" data-id="element-175">
            <LineChart data={machine.performanceHistory} data-id="element-176" />
          </div>
        </div>
        <div className="bg-gray-800 rounded-xl p-4 border border-gray-700 shadow-lg backdrop-blur-sm bg-opacity-60" data-id="element-177">
          <h3 className="text-lg font-medium mb-4 text-gray-200" data-id="element-178">
            Aktuelle Auslastung
          </h3>
          <div className="h-64 flex items-center justify-center" data-id="element-179">
            <GaugeChart value={machine.metrics.currentLoad} data-id="element-180" />
          </div>
        </div>
      </div>

      {/* Fassverarbeitungs-Tracking für die Waschanlage */}
      {machine.type === 'waschanlage' && <>
          <WaschanlageBarrelTracking data-id="element-181" />
          <BarrelLabelImages data-id="element-182" />
        </>}

      {/* Neue Sektion für Abfälle */}
      {wasteProduction.length > 0 && <div className="bg-gray-800 rounded-xl p-6 border border-gray-700 shadow-lg backdrop-blur-sm bg-opacity-60" data-id="element-183">
          <div className="flex items-center justify-between mb-4" data-id="element-184">
            <h3 className="text-xl font-medium text-gray-200 flex items-center gap-2" data-id="element-185">
              <Recycle className="text-red-400" data-id="element-186" />
              Anfallende Abfälle zur Entsorgung
            </h3>
          </div>
          <div className="space-y-4" data-id="element-187">
            {wasteProduction.map((waste, index) => <div key={index} className="bg-gray-700/40 p-4 rounded-lg border border-gray-600/50" data-id="element-188">
                <div className="flex items-center gap-3 mb-2" data-id="element-189">
                  {waste.icon}
                  <span className="text-lg font-medium text-white" data-id="element-190">
                    {waste.name}
                  </span>
                </div>
                <div className="grid grid-cols-2 gap-4 mt-3" data-id="element-191">
                  <div className="flex flex-col" data-id="element-192">
                    <span className="text-gray-400 text-sm" data-id="element-193">Pro Fass</span>
                    <span className="text-white font-medium text-lg" data-id="element-194">
                      {waste.amount} {waste.unit}
                    </span>
                  </div>
                  <div className="flex flex-col" data-id="element-195">
                    <span className="text-gray-400 text-sm" data-id="element-196">Pro Stunde</span>
                    <span className="text-white font-medium text-lg" data-id="element-197">
                      {calculateHourlyConsumption(waste.amount, waste.unit)}
                    </span>
                  </div>
                </div>
              </div>)}
          </div>
        </div>}

      {renderMachineSpecificDetails()}

      <MachineDetails machine={machine} data-id="element-198" />
    </div>;
};